import React, { useMemo, useState } from 'react';
import { css } from '@emotion/css';
import capitalize from 'lodash/capitalize';

import { useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import Form from '@/components/Form';
import { DEFAULT_SCHEDULE_INFO_INPUT } from '@/components/Form/components/Frequency';
import { Modal } from '@/components/Modals';
import useResponse from '@/hooks/useResponse';
import { useCreateMeetingMutation } from '@/modules/1on1s/graphql/mutation/generated/createMeeting';
import { useUpdateMeetingMutation } from '@/modules/1on1s/graphql/mutation/generated/updateMeeting';
import useMeetingEvents from '@/modules/1on1s/hooks/useMeetingEvents';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { MeetingTypeEnum, PublicMeetingStatusEnum } from '@/types/schema';
import { getErrorMessage } from '@/utils/api/response';

import { MeetingFormType, SetupMeetingModalProps } from './types';
import { getFields, translationMap, validateMeetingForm } from './utils';

const modalStyles = css`
  width: 54rem;
  height: 66rem;
`;

const SetupMeetingModal: React.FC<SetupMeetingModalProps> = (props) => {
  const { initialValues, meetingId, mode, onClose, onSuccess } = props;
  const user = useUserContext();
  const { t } = useTranslation('Meeting');
  const [selectedValues, setSelectedValues] = React.useState<MeetingFormType | undefined>(
    undefined,
  );
  const { onTrackTMEvent } = useMeetingEvents();

  const initialFormValues: MeetingFormType = useMemo(
    () => ({
      participantIds: initialValues?.participantIds ?? [user.id],
      schedule: initialValues?.schedule ?? DEFAULT_SCHEDULE_INFO_INPUT,
      name: initialValues?.name ?? '',
    }),
    [initialValues?.name, initialValues?.participantIds, initialValues?.schedule, user.id],
  );

  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { handleResponse } = useResponse();
  const [{ fetching }, createMeeting] = useCreateMeetingMutation();
  const [{ fetching: updating }, updateMeeting] = useUpdateMeetingMutation();
  const [submitRef, setSubmitRef] = useState<() => void>(() => {});

  const onSubmit = React.useCallback(async () => {
    if (!selectedValues) return;
    const participantIds = selectedValues.participantIds;

    const res =
      mode === 'create'
        ? await createMeeting({
            data: {
              participantIds,
              schedule: selectedValues.schedule,
              type: MeetingTypeEnum.Team,
              name: selectedValues.name,
            },
          })
        : await updateMeeting({
            data: {
              participantIds,
              name: selectedValues.name,
              meetingId: meetingId ?? '',
              schedule: selectedValues.schedule,
              status: PublicMeetingStatusEnum.Active,
            },
          });

    handleResponse({
      condition: !res.error,
      onSuccess: () => {
        showSnackbar({
          message: (
            <Trans
              i18nKey={translationMap[mode].snackbarText}
              values={{ name: selectedValues.name }}
            />
          ),
        });
        onTrackTMEvent('onScheduleTM');
        onSuccess?.();
        onClose();
      },
      onError: () => {
        showErrorSnackbar({
          message: capitalize(getErrorMessage(res.error?.graphQLErrors)),
        });
      },
    });
  }, [
    createMeeting,
    handleResponse,
    meetingId,
    mode,
    onClose,
    onSuccess,
    onTrackTMEvent,
    selectedValues,
    showErrorSnackbar,
    showSnackbar,
    updateMeeting,
  ]);

  const onChange = React.useCallback(({ values }: any) => {
    setSelectedValues(values);
  }, []);

  const fields = getFields({ t, user });
  const validate = (values: MeetingFormType) => validateMeetingForm(t, values);

  return (
    <Modal
      action={{
        discardText: t('Common:Cancel'),
        text: t(translationMap[mode].ctaButtonLabel),
        onSubmit: submitRef,
        appearance: 'primary',
        isSubmitting: fetching || updating,
      }}
      className="px-32 pb-20"
      classes={{
        paper: modalStyles,
      }}
      header={{
        title: t(translationMap[mode].title),
        description: (
          <Trans
            i18nKey={translationMap[mode].description}
            values={{ name: initialValues?.name }}
            variant="label-01"
          />
        ),
        classes: {
          description: 'mb-32',
        },
      }}
      showCloseBtn={true}
      onClose={onClose}>
      <Form<MeetingFormType>
        fields={fields}
        formName="SetupMeeting"
        initialValues={initialFormValues}
        overrides={{
          spacing: {
            row: 'space-y-24',
          },
        }}
        setSubmitRef={setSubmitRef}
        validate={validate}
        onChange={onChange}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default SetupMeetingModal;
